import { useState } from 'react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from '~/primitives/dropdown-menu';
import { cn } from '~/util';
import { useFieldContext } from '~/util/hooks/useFormContext';
import {
    FieldLabelWithError,
    FieldWithErrorProps,
} from './FieldLabelWithError';
import { useDisableFormContext } from '../DisableContext';
import { InteractableDataWrapper } from '~/partials/InteractableDataWrapper';

export function DropdownField({
    options,
    optionsDisplay,
    placeholder,
    classname,
    allowNull = false,
    dropdownOpen,
    setDropdownOpen,
    preventCloseAutoFocus = false,
    ...labelProps
}: {
    options: readonly string[];
    optionsDisplay?: readonly string[];
    placeholder: string;
    classname?: string;
    allowNull?: boolean;
    dropdownOpen?: boolean;
    setDropdownOpen?: (open: boolean) => void;
    preventCloseAutoFocus?: boolean;
} & FieldWithErrorProps) {
    type optionsType = (typeof options)[number] | null;
    const field = useFieldContext<optionsType>();
    const [internalDropdownOpen, setInternalDropdownOpen] = useState(false);

    const [usedDropdownOpen, setUsedDropdownOpen] =
        dropdownOpen !== undefined && setDropdownOpen !== undefined
            ? [dropdownOpen, setDropdownOpen]
            : [internalDropdownOpen, setInternalDropdownOpen];

    const disabled = useDisableFormContext();

    const availableOptions = allowNull ? [null, ...options] : options;
    const availableOptionsDisplay = allowNull
        ? [placeholder, ...(optionsDisplay ?? options)]
        : (optionsDisplay ?? options);

    const selectedIndex = availableOptions.indexOf(field.state.value as string);

    return (
        <FieldLabelWithError<optionsType> {...labelProps}>
            <DropdownMenu
                open={usedDropdownOpen}
                onOpenChange={setUsedDropdownOpen}
            >
                <DropdownMenuTrigger disabled={disabled}>
                    <InteractableDataWrapper
                        id={field.name}
                        disabled={disabled}
                        open={usedDropdownOpen}
                        className={cn(
                            field.state.value
                                ? 'text-secondary-foreground'
                                : 'text-secondary-light',
                            classname,
                            disabled && 'opacity-50 cursor-not-allowed',
                        )}
                        required={labelProps.required}
                    >
                        {selectedIndex >= 0
                            ? availableOptionsDisplay[selectedIndex]
                            : placeholder}
                    </InteractableDataWrapper>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="start"
                    onCloseAutoFocus={
                        preventCloseAutoFocus
                            ? (event) => event.preventDefault()
                            : undefined
                    }
                >
                    <DropdownMenuRadioGroup
                        value={field.state.value ?? ''}
                        onValueChange={(value) => {
                            field.handleChange(value === '' ? null : value);
                            setUsedDropdownOpen(false);
                        }}
                    >
                        {availableOptions.map((option, index) => (
                            <DropdownMenuRadioItem
                                key={option ?? ''}
                                value={option ?? ''}
                                className="text-lg cursor-pointer"
                            >
                                {availableOptionsDisplay[index] ??
                                    availableOptions[index] ??
                                    placeholder}
                            </DropdownMenuRadioItem>
                        ))}
                    </DropdownMenuRadioGroup>
                </DropdownMenuContent>
            </DropdownMenu>
        </FieldLabelWithError>
    );
}
